<script setup>
import { ref, computed, watch } from 'vue'
import { Head, Link, useForm } from '@inertiajs/vue3'
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
import MuxVideoUploadViewer from '@/Components/MuxVideoUploadViewer.vue'
import PdfDocumentUpload from '@/Components/Forms/PdfDocumentUpload.vue'
import { Button } from '@/Components/ui/button'
import { Input } from '@/Components/ui/input'
import { Label } from '@/Components/ui/label'
import { Alert, AlertDescription } from '@/Components/ui/alert'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/Components/ui/select'
import { TagsInput, TagsInputInput, TagsInputItem, TagsInputItemDelete, TagsInputItemText } from '@/Components/ui/tags-input'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/Components/ui/tabs'
import { AlertCircle } from 'lucide-vue-next'
import teacher from '@/routes/teacher'

const props = defineProps({
  subjects: Array,
  examboards: Array,
  levels: Array,
})

// Content type tab: 'video' or 'document'
const activeTab = ref('video')

const form = useForm({
  type: 1,
  title: '',
  subject_id: '',
  examboard_id: '',
  level_id: '',
  topics: '',
  mux_upload_id: '',
  document_path: '',
  document_original_name: '',
  document_size: 0,
  attachments: [],
})

// Sync form type when tab changes
watch(activeTab, (tab) => {
  form.type = tab === 'video' ? 1 : 2
})

const hasErrors = computed(() => Object.keys(form.errors).length > 0)

// Topics management
const topicsList = ref([])

const updateFormTopics = () => {
  form.topics = topicsList.value.join(',')
}

// Video upload state
const isVideoUploading = ref(false)
const videoError = ref(false)

const handleVideoUploadStart = () => {
  isVideoUploading.value = true
  videoError.value = false
}

const handleVideoUploadComplete = () => {
  isVideoUploading.value = false
  videoError.value = false
}

const handleVideoUploadError = () => {
  isVideoUploading.value = false
}

// Document upload state
const isDocUploading = ref(false)
const docError = ref(false)

const handleDocUploadStart = () => {
  isDocUploading.value = true
  docError.value = false
}

const handleDocUploadComplete = () => {
  isDocUploading.value = false
  docError.value = false
}

const handleDocUploadError = () => {
  isDocUploading.value = false
}

// Attachment (PDF for video content) state
const isAttachmentUploading = ref(false)
const attachmentPath = ref('')
const attachmentName = ref('')
const attachmentSize = ref(0)

const onAttachmentUploadComplete = (result) => {
  isAttachmentUploading.value = false
  form.attachments.push({
    path: result.path,
    original_name: result.original_name,
    size: result.size,
  })
  // Reset the upload component
  attachmentPath.value = ''
  attachmentName.value = ''
  attachmentSize.value = 0
}

const removeAttachment = (index) => {
  form.attachments.splice(index, 1)
}

const isUploading = computed(() => isVideoUploading.value || isDocUploading.value || isAttachmentUploading.value)

const submitForm = () => {
  if (form.type === 1 && !form.mux_upload_id) {
    videoError.value = true
    return
  }

  if (form.type === 2 && !form.document_path) {
    docError.value = true
    return
  }

  videoError.value = false
  docError.value = false

  form.post(teacher.content.store().url, {
    preserveScroll: true,
    onError: (errors) => {
      if (errors.mux_upload_id) videoError.value = true
      if (errors.document_path) docError.value = true
    }
  })
}
</script>

<template>
  <Head title="Upload Learning Material" />

  <DashboardLayout>
    <div class="flex py-6 px-4 md:px-8 font-sans">
      <div class="w-full flex flex-col gap-6">
        <!-- Breadcrumb -->
        <nav class="flex items-center gap-2 text-sm text-gray-500">
          <Link :href="teacher.content.index().url" class="font-medium hover:underline hover:text-gray-700">
            My Content
          </Link>
          <span class="font-medium">/</span>
          <span class="text-gray-900 font-medium">Upload</span>
        </nav>

        <!-- Header -->
        <div class="flex flex-col gap-2 mb-4">
          <h1 class="text-gray-900 text-3xl md:text-4xl font-bold tracking-tight">Upload Learning Material</h1>
          <p class="text-gray-500 text-base">Share your knowledge with students by uploading videos or documents.</p>
        </div>

        <!-- Form -->
        <form @submit.prevent="submitForm" class="flex flex-col gap-8">
          <!-- Form Fields Grid -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <!-- Title -->
            <div class="md:col-span-3 flex flex-col gap-2">
              <Label class="text-gray-900 text-sm font-semibold">Resource Title</Label>
              <Input
                v-model="form.title"
                required
                :class="form.errors.title ? '!border-red-300' : ''"
                placeholder="e.g., Introduction to Calculus - Derivatives"
              />
              <p v-if="form.errors.title" class="text-sm text-red-600">{{ form.errors.title }}</p>
            </div>

            <!-- Subject -->
            <div class="flex flex-col gap-2">
              <Label class="text-gray-900 text-sm font-semibold">Subject</Label>
              <Select v-model="form.subject_id" required>
                <SelectTrigger :class="form.errors.subject_id ? '!border-red-300' : ''">
                  <SelectValue placeholder="Select Subject..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem v-for="subject in subjects" :key="subject.id" :value="subject.id.toString()">
                    {{ subject.name }}
                  </SelectItem>
                </SelectContent>
              </Select>
              <p v-if="form.errors.subject_id" class="text-sm text-red-600">{{ form.errors.subject_id }}</p>
            </div>

            <!-- Exam Board -->
            <div class="flex flex-col gap-2">
              <Label class="text-gray-900 text-sm font-semibold">Exam Board</Label>
              <Select v-model="form.examboard_id" required>
                <SelectTrigger :class="form.errors.examboard_id ? '!border-red-300' : ''">
                  <SelectValue placeholder="Select Board..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem v-for="examboard in examboards" :key="examboard.id" :value="examboard.id.toString()">
                    {{ examboard.name }}
                  </SelectItem>
                </SelectContent>
              </Select>
              <p v-if="form.errors.examboard_id" class="text-sm text-red-600">{{ form.errors.examboard_id }}</p>
            </div>

            <!-- Level -->
            <div class="flex flex-col gap-2">
              <Label class="text-gray-900 text-sm font-semibold">Level</Label>
              <Select v-model="form.level_id" required>
                <SelectTrigger :class="form.errors.level_id ? '!border-red-300' : ''">
                  <SelectValue placeholder="Select Level..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem v-for="level in levels" :key="level.id" :value="level.id.toString()">
                    {{ level.name }}
                  </SelectItem>
                </SelectContent>
              </Select>
              <p v-if="form.errors.level_id" class="text-sm text-red-600">{{ form.errors.level_id }}</p>
            </div>

            <!-- Topics/Keywords -->
            <div class="md:col-span-3 flex flex-col gap-2">
              <Label class="text-gray-900 text-sm font-semibold">
                Topics/keywords <span class="text-gray-400 font-normal">(Max 5)</span>
              </Label>
              <TagsInput
                v-model="topicsList"
                @update:model-value="updateFormTopics"
                :max="5"
                class="h-[45px]"
              >
                <TagsInputItem v-for="item in topicsList" :key="item" :value="item">
                  <TagsInputItemText />
                  <TagsInputItemDelete />
                </TagsInputItem>
                <TagsInputInput
                  placeholder="Type and press Enter..."
                  :disabled="topicsList.length >= 5"
                />
              </TagsInput>
              <p class="text-xs text-gray-500">Tags help students find your content faster. Press Enter or comma to add.</p>
            </div>
          </div>

          <!-- Content Type Tabs -->
          <div>
            <Label class="text-gray-900 text-sm font-semibold mb-3 block">Content Type</Label>
            <Tabs v-model="activeTab" class="w-full">
              <TabsList class="grid w-full grid-cols-2 max-w-md h-12 bg-gray-100 p-1 rounded-xl">
                <TabsTrigger
                  value="video"
                  class="flex items-center gap-2 rounded-lg text-sm font-semibold data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md"
                >
                  <span class="material-symbols-outlined text-base">videocam</span>
                  Video
                </TabsTrigger>
                <TabsTrigger
                  value="document"
                  class="flex items-center gap-2 rounded-lg text-sm font-semibold data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md"
                >
                  <span class="material-symbols-outlined text-base">description</span>
                  Document
                </TabsTrigger>
              </TabsList>

            <!-- Video Tab -->
            <TabsContent value="video" class="mt-6 flex flex-col gap-6">
              <div>
                <Label class="text-gray-900 text-sm font-semibold mb-3 block">Upload Video</Label>
                <MuxVideoUploadViewer
                  v-model="form.mux_upload_id"
                  :title="form.title"
                  :show-error="videoError"
                  @upload:start="handleVideoUploadStart"
                  @upload:complete="handleVideoUploadComplete"
                  @upload:error="handleVideoUploadError"
                >
                  <template #error>
                    <p v-if="videoError" class="text-red-600 text-sm">Please upload a video before publishing.</p>
                  </template>
                </MuxVideoUploadViewer>
              </div>

              <!-- Optional PDF Attachment for Video -->
              <div>
                <Label class="text-gray-900 text-sm font-semibold mb-3 block">
                  Supporting Document <span class="text-gray-400 font-normal">(Optional)</span>
                </Label>
                <p class="text-xs text-gray-500 mb-3">Attach a PDF document as supporting material for this video (e.g., notes, worksheets).</p>

                <!-- Uploaded Attachments List -->
                <div v-if="form.attachments.length > 0" class="flex flex-col gap-2 mb-3">
                  <div
                    v-for="(attachment, index) in form.attachments"
                    :key="index"
                    class="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3"
                  >
                    <div class="flex items-center gap-3 min-w-0">
                      <span class="material-symbols-outlined text-red-500 text-xl shrink-0">picture_as_pdf</span>
                      <div class="min-w-0">
                        <p class="text-sm font-medium text-gray-900 truncate">{{ attachment.original_name }}</p>
                        <p v-if="attachment.size" class="text-xs text-gray-500">
                          {{ (attachment.size / (1024 * 1024)).toFixed(1) }} MB
                        </p>
                      </div>
                    </div>
                    <Button type="button" variant="ghost" size="sm" @click="removeAttachment(index)" class="text-red-600 hover:text-red-700 hover:bg-red-50">
                      <span class="material-symbols-outlined text-sm">close</span>
                    </Button>
                  </div>
                </div>

                <!-- Attachment Upload (show only if no attachment yet) -->
                <PdfDocumentUpload
                  v-if="form.attachments.length === 0"
                  v-model="attachmentPath"
                  v-model:original-name="attachmentName"
                  v-model:file-size="attachmentSize"
                  label="Upload Supporting Document"
                  @upload:start="isAttachmentUploading = true"
                  @upload:complete="onAttachmentUploadComplete"
                  @upload:error="isAttachmentUploading = false"
                />
              </div>
            </TabsContent>

            <!-- Document Tab -->
            <TabsContent value="document" class="mt-6">
              <div>
                <Label class="text-gray-900 text-sm font-semibold mb-3 block">Upload Document</Label>
                <PdfDocumentUpload
                  v-model="form.document_path"
                  v-model:original-name="form.document_original_name"
                  v-model:file-size="form.document_size"
                  :show-error="docError"
                  :required="true"
                  @upload:start="handleDocUploadStart"
                  @upload:complete="handleDocUploadComplete"
                  @upload:error="handleDocUploadError"
                >
                  <template #error>
                    <p v-if="docError" class="text-red-600 text-sm">Please upload a document before publishing.</p>
                  </template>
                </PdfDocumentUpload>
              </div>
            </TabsContent>
            </Tabs>
          </div>

          <!-- General Error Alert -->
          <Alert v-if="hasErrors" variant="destructive">
            <AlertCircle class="h-4 w-4" />
            <AlertDescription>
              Please correct the errors below before submitting.
            </AlertDescription>
          </Alert>

          <!-- Action Buttons -->
          <div class="flex items-center justify-end gap-2 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              as-child
            >
              <Link :href="teacher.content.index().url">
                Cancel
              </Link>
            </Button>
            <Button
              type="submit"
              :disabled="isUploading || form.processing"
            >
              <span v-if="form.processing" class="material-symbols-outlined animate-spin mr-2">sync</span>
              <span v-else class="material-symbols-outlined mr-2">check</span>
              {{ form.processing ? 'Publishing...' : 'Publish Content' }}
            </Button>
          </div>
        </form>
      </div>
    </div>
  </DashboardLayout>
</template>
